import { useId, useState, type FormEvent } from 'react';
import { TIPOS_PROPIEDAD } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';

interface Props {
  idioma: Idioma;
  onCreada: (id: string) => void;
  onCancelar: () => void;
}

/**
 * Alta de propiedad.
 *
 * Solo pide lo imprescindible para existir: codigo, nombre, pais, zona horaria y
 * tipo. Todo lo demas se completa despues, y la ficha va diciendo que falta para
 * poder activarla. Pedirlo todo de golpe garantiza que nadie de nada de alta.
 */
export function NuevaPropiedad({ idioma, onCreada, onCancelar }: Props) {
  const ids = {
    codigo: useId(),
    nombre: useId(),
    pais: useId(),
    zonaHoraria: useId(),
    tipo: useId(),
    zona: useId(),
    direccion: useId(),
  };

  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [pais, setPais] = useState('ES');
  const [zonaHoraria, setZonaHoraria] = useState('Europe/Madrid');
  const [tipoPropiedad, setTipoPropiedad] = useState<(typeof TIPOS_PROPIEDAD)[number]>('VILLA');
  const [zona, setZona] = useState('');
  const [direccion, setDireccion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const enviar = async (evento: FormEvent) => {
    evento.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      const { propiedad } = await api.crearPropiedad({
        codigo,
        nombre,
        pais,
        zonaHoraria,
        tipoPropiedad,
        ...(zona ? { zona } : {}),
        ...(direccion ? { direccion } : {}),
      });
      onCreada(propiedad.id);
    } catch (fallo: unknown) {
      setError(
        fallo instanceof ErrorApi ? t(idioma, fallo.mensajeClave) : t(idioma, 'error.servidor'),
      );
    } finally {
      setEnviando(false);
    }
  };

  return (
    <main className="tarjeta">
      <h1>{t(idioma, 'propiedades.titulo')}</h1>
      <form className="formulario" onSubmit={(e) => void enviar(e)}>
        <div className="campo">
          <label htmlFor={ids.codigo}>{t(idioma, 'propiedades.codigo')}</label>
          <input
            id={ids.codigo}
            required
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
          />
        </div>

        <div className="campo">
          <label htmlFor={ids.nombre}>{t(idioma, 'propiedades.nombre')}</label>
          <input
            id={ids.nombre}
            required
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
          />
        </div>

        <div className="campo">
          <label htmlFor={ids.pais}>{t(idioma, 'party.pais')}</label>
          <input
            id={ids.pais}
            required
            maxLength={2}
            value={pais}
            onChange={(e) => setPais(e.target.value)}
          />
        </div>

        <div className="campo">
          <label htmlFor={ids.zonaHoraria}>Zona horaria</label>
          <input
            id={ids.zonaHoraria}
            required
            value={zonaHoraria}
            onChange={(e) => setZonaHoraria(e.target.value)}
          />
        </div>

        <div className="campo">
          <label htmlFor={ids.tipo}>Tipo</label>
          <select
            id={ids.tipo}
            value={tipoPropiedad}
            onChange={(e) => setTipoPropiedad(e.target.value as typeof tipoPropiedad)}
          >
            {TIPOS_PROPIEDAD.map((tipo) => (
              <option key={tipo} value={tipo}>
                {tipo}
              </option>
            ))}
          </select>
        </div>

        <div className="campo">
          <label htmlFor={ids.zona}>{t(idioma, 'propiedades.zona')}</label>
          <input id={ids.zona} value={zona} onChange={(e) => setZona(e.target.value)} />
        </div>

        <div className="campo">
          <label htmlFor={ids.direccion}>{t(idioma, 'requisito.DIRECCION')}</label>
          <input
            id={ids.direccion}
            value={direccion}
            onChange={(e) => setDireccion(e.target.value)}
          />
        </div>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <div className="acciones">
          <button type="submit" disabled={enviando}>
            {enviando ? t(idioma, 'app.cargando') : t(idioma, 'app.guardar')}
          </button>
          <button type="button" className="secundario" onClick={onCancelar}>
            {t(idioma, 'app.cancelar')}
          </button>
        </div>
      </form>
    </main>
  );
}
